import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { 
  X, ChevronLeft, ChevronRight, Star, Check, XIcon, 
  RotateCcw, Bookmark, MessageSquare, Columns, Maximize2,
  Download, Home
} from 'lucide-react';
import { toast } from 'sonner';

const PresentationMode = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [allItems, setAllItems] = useState([]);
  const [compareMode, setCompareMode] = useState(false);
  const [compareIndex, setCompareIndex] = useState(1);
  const [showComments, setShowComments] = useState(false);
  const [currentComment, setCurrentComment] = useState('');
  const [currentRating, setCurrentRating] = useState(0);
  const [clientName, setClientName] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(`${API}/review/${shareToken}/presentation`);
        setData(response.data);
        
        // Flatten all items for navigation
        const items = [];
        response.data.environments?.forEach(env => {
          env.items?.forEach(item => {
            items.push({ ...item, environmentName: env.name });
          });
        });
        response.data.general_items?.forEach(item => {
          items.push({ ...item, environmentName: null });
        });
        setAllItems(items);
      } catch (error) {
        console.error('Failed to fetch presentation data:', error);
        toast.error('Failed to load presentation');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [shareToken]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      } else if (e.key === 'Escape') {
        if (isFullscreen) {
          document.exitFullscreen?.();
        } else {
          navigate(-1);
        }
      } else if (e.key === 'c' || e.key === 'C') {
        setCompareMode(!compareMode);
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, allItems.length, compareMode, isFullscreen]);

  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => (prev < allItems.length - 1 ? prev + 1 : 0));
  }, [allItems.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : allItems.length - 1));
  }, [allItems.length]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const submitFeedback = async (action) => {
    const currentItem = allItems[currentIndex];
    if (!currentItem) return;

    try {
      await axios.post(`${API}/review/${shareToken}/items/${currentItem.item_id}`, {
        status: action,
        rating: currentRating || null,
        comment: currentComment || null,
        client_name: clientName || null
      });
      
      // Update local state
      setAllItems(prev => prev.map((item, idx) => 
        idx === currentIndex 
          ? { ...item, client_status: action, client_rating: currentRating, client_comment: currentComment }
          : item
      ));
      
      toast.success(action === 'approved' ? 'Approved!' : action === 'rejected' ? 'Rejected' : 'Feedback saved');
      setCurrentComment('');
      setCurrentRating(0);
      
      // Auto-advance after feedback
      if (currentIndex < allItems.length - 1) {
        setTimeout(() => nextSlide(), 500);
      }
    } catch (error) {
      toast.error('Failed to submit feedback');
    }
  };

  const downloadSummary = async () => {
    try {
      // Download PDF directly
      const response = await axios.get(`${API}/review/${shareToken}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${data.board?.project_name || data.board?.name || 'material-selection'}_summary.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded');
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center">
        <div className="w-12 h-12 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!data || allItems.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center text-white">
        <div className="text-center">
          <h1 className="text-2xl font-light mb-4">No materials to present</h1>
          <Button variant="outline" onClick={() => navigate(-1)} className="text-white border-white">
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const currentItem = allItems[currentIndex];
  const compareItem = compareMode && allItems[compareIndex] ? allItems[compareIndex] : null;

  return (
    <div className="fixed inset-0 bg-black text-white overflow-hidden" data-testid="presentation-mode">
      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 z-50 p-4 flex items-center justify-between bg-gradient-to-b from-black/80 to-transparent">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => {
              if (window.history.length > 2) {
                navigate(-1);
              } else {
                navigate(`/review/${shareToken}`);
              }
            }}
            className="text-white hover:bg-white/10"
            data-testid="exit-presentation"
          >
            <X className="w-6 h-6" />
          </Button>
          <div>
            <h1 className="text-lg font-medium">{data.board?.name}</h1>
            {data.board?.project_name && (
              <p className="text-sm text-white/60">{data.board.project_name}</p>
            )}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-white/60 mr-4">
            {currentIndex + 1} / {allItems.length}
          </span>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => setCompareMode(!compareMode)}
            className={`text-white hover:bg-white/10 ${compareMode ? 'bg-white/20' : ''}`}
            title="Compare Mode (C)"
          >
            <Columns className="w-5 h-5" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={toggleFullscreen}
            className="text-white hover:bg-white/10"
            title="Fullscreen (F)"
          >
            <Maximize2 className="w-5 h-5" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon"
            onClick={downloadSummary}
            className="text-white hover:bg-white/10"
            title="Download Summary"
          >
            <Download className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className={`flex h-full pt-20 pb-32 ${compareMode ? 'gap-4 px-4' : ''}`}>
        {/* Current Material */}
        <div className={`flex-1 flex flex-col items-center justify-center ${compareMode ? 'max-w-[50%]' : ''}`}>
          <div className="relative w-full h-full max-w-5xl mx-auto px-8">
            <img 
              src={currentItem.material?.images?.[0]} 
              alt={currentItem.material?.name}
              className="w-full h-full object-contain"
            />
          </div>
          
          {/* Material Info Overlay */}
          <div className="absolute bottom-32 left-0 right-0 px-8 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-16 pb-4">
            <div className={`max-w-5xl mx-auto ${compareMode ? 'text-center' : ''}`}>
              {currentItem.environmentName && (
                <p className="text-xs uppercase tracking-widest text-white/60 mb-1">
                  {currentItem.environmentName}
                  {currentItem.application_area && ` · ${currentItem.application_area}`}
                </p>
              )}
              <h2 className="text-4xl md:text-5xl font-light mb-2">{currentItem.material?.name}</h2>
              <p className="text-lg text-white/70">{currentItem.material?.stone_type}</p>
              
              {/* Status Badge */}
              {currentItem.client_status && currentItem.client_status !== 'pending' && (
                <div className={`inline-flex items-center gap-2 mt-3 px-4 py-2 text-sm uppercase tracking-wider ${
                  currentItem.client_status === 'approved' ? 'bg-green-600' :
                  currentItem.client_status === 'rejected' ? 'bg-red-600' :
                  'bg-yellow-600'
                }`}>
                  {currentItem.client_status === 'approved' && <Check className="w-4 h-4" />}
                  {currentItem.client_status === 'rejected' && <XIcon className="w-4 h-4" />}
                  {currentItem.client_status}
                  {currentItem.client_rating && ` · ${currentItem.client_rating}/5`}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Compare Material */}
        {compareMode && compareItem && (
          <div className="flex-1 max-w-[50%] flex flex-col items-center justify-center border-l border-white/20">
            <div className="relative w-full h-full max-w-5xl mx-auto px-8">
              <img 
                src={compareItem.material?.images?.[0]} 
                alt={compareItem.material?.name}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="absolute bottom-32 right-0 w-1/2 px-8 bg-gradient-to-t from-black/80 via-black/40 to-transparent pt-16 pb-4">
              <div className="max-w-5xl mx-auto text-center">
                <h2 className="text-4xl md:text-5xl font-light mb-2">{compareItem.material?.name}</h2>
                <p className="text-lg text-white/70">{compareItem.material?.stone_type}</p>
              </div>
            </div>
            
            {/* Compare selector */}
            <div className="absolute top-24 right-4 flex gap-2">
              {allItems.map((item, idx) => idx !== currentIndex && (
                <button
                  key={idx}
                  onClick={() => setCompareIndex(idx)}
                  className={`w-16 h-16 overflow-hidden border-2 ${compareIndex === idx ? 'border-white' : 'border-white/30'}`}
                >
                  <img src={item.material?.images?.[0]} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 p-4 bg-white/10 hover:bg-white/20 transition-colors"
        data-testid="prev-slide"
      >
        <ChevronLeft className="w-8 h-8" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 p-4 bg-white/10 hover:bg-white/20 transition-colors"
        data-testid="next-slide"
      >
        <ChevronRight className="w-8 h-8" />
      </button>

      {/* Bottom Feedback Bar */}
      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent pt-8 pb-6 px-8 z-40">
        <div className="max-w-4xl mx-auto">
          {/* Client Name Input (one time) */}
          {!clientName && (
            <div className="mb-6 flex items-center justify-center">
              <input
                type="text"
                placeholder="Your name (optional)"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                className="bg-white/10 border border-white/20 px-4 py-2 text-white placeholder-white/50 focus:outline-none focus:border-white/50 w-64 text-center"
              />
            </div>
          )}
          
          {/* Rating */}
          <div className="flex items-center justify-center gap-2 mb-4">
            {[1, 2, 3, 4, 5].map(star => (
              <button
                key={star}
                onClick={() => setCurrentRating(star)}
                className="p-1 hover:scale-110 transition-transform"
              >
                <Star className={`w-8 h-8 ${currentRating >= star ? 'fill-yellow-400 text-yellow-400' : 'text-white/40'}`} />
              </button>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-center gap-4">
            <Button
              onClick={() => submitFeedback('approved')}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-6 text-lg"
              data-testid="approve-btn"
            >
              <Check className="w-5 h-5 mr-2" /> Approve
            </Button>
            <Button
              onClick={() => submitFeedback('rejected')}
              variant="outline"
              className="border-red-500 text-red-400 hover:bg-red-500/20 px-8 py-6 text-lg"
              data-testid="reject-btn"
            >
              <XIcon className="w-5 h-5 mr-2" /> Reject
            </Button>
            <Button
              onClick={() => submitFeedback('request_alternative')}
              variant="outline"
              className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/20 px-6 py-6"
              data-testid="alternative-btn"
            >
              <RotateCcw className="w-5 h-5 mr-2" /> Alternative
            </Button>
            <Button
              onClick={() => submitFeedback('save_for_later')}
              variant="outline"
              className="border-white/50 text-white/70 hover:bg-white/10 px-6 py-6"
            >
              <Bookmark className="w-5 h-5 mr-2" /> Save
            </Button>
            <Button
              onClick={() => setShowComments(!showComments)}
              variant="ghost"
              className={`text-white/70 hover:bg-white/10 px-6 py-6 ${showComments ? 'bg-white/10' : ''}`}
            >
              <MessageSquare className="w-5 h-5 mr-2" /> Comment
            </Button>
          </div>

          {/* Comment Input */}
          {showComments && (
            <div className="mt-4 max-w-2xl mx-auto">
              <Textarea
                placeholder="Add a comment about this material..."
                value={currentComment}
                onChange={(e) => setCurrentComment(e.target.value)}
                className="bg-white/10 border-white/20 text-white placeholder-white/50 min-h-[80px]"
              />
            </div>
          )}
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {allItems.slice(0, 20).map((item, idx) => (
          <button
            key={idx}
            onClick={() => setCurrentIndex(idx)}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === currentIndex ? 'bg-white w-8' : 'bg-white/40 hover:bg-white/60'
            }`}
          />
        ))}
        {allItems.length > 20 && <span className="text-white/40 text-xs ml-2">+{allItems.length - 20}</span>}
      </div>

      {/* Keyboard Hints */}
      <div className="absolute bottom-4 right-4 text-xs text-white/30 space-x-4">
        <span>← → Navigate</span>
        <span>C Compare</span>
        <span>F Fullscreen</span>
        <span>ESC Exit</span>
      </div>
    </div>
  );
};

export default PresentationMode;
